import React, { useEffect, useState } from "react";
import { api, formatErr, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusPill, EmptyState } from "@/components/shared";
import { Plus, Search, Edit3, Trash2, Car } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Available", "Reserved", "Rented", "Maintenance", "Disabled"];

const empty = {
  year: new Date().getFullYear(),
  make: "",
  model: "",
  vin: "",
  plate: "",
  mileage: 0,
  status: "Available",
  daily_rate: 0,
  weekly_rate: 0,
  deposit_amount: 0,
  gps_device_id: "",
  insurance_status: "",
  registration_expiration: "",
  inspection_expiration: "",
  maintenance_notes: "",
};

export default function Vehicles() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/vehicles").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (v) => {
    setEditing({ ...empty, ...v });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...editing };
      payload.year = Number(payload.year);
      payload.mileage = Number(payload.mileage);
      payload.daily_rate = Number(payload.daily_rate);
      payload.weekly_rate = Number(payload.weekly_rate);
      payload.deposit_amount = Number(payload.deposit_amount);
      const id = payload.id;
      delete payload.id;
      delete payload.created_at;
      if (id) {
        await api.put(`/vehicles/${id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const filtered = rows.filter((v) => {
    const text = `${v.year} ${v.make} ${v.model} ${v.plate} ${v.vin}`.toLowerCase();
    if (filter !== "All" && v.status !== filter) return false;
    return text.includes(q.toLowerCase());
  });

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Vehicles"
        subtitle="Fleet inventory"
        testId="vehicles-page-header"
        action={
          <Button data-testid="add-vehicle-button" onClick={openNew} className="bg-black hover:bg-gray-800 text-white">
            <Plus size={16} className="mr-2" /> Add Vehicle
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search year, make, model, plate…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="vehicle-search-input" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`vehicle-filter-${s.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === s ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No vehicles" description="Add a vehicle to your fleet." testId="vehicles-empty" action={<Button onClick={openNew}>Add vehicle</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="vehicles-list">
          {filtered.map((v) => (
            <div key={v.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-md bg-gray-100 grid place-items-center shrink-0">
                  <Car size={20} className="text-gray-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading font-semibold truncate">
                      {v.year} {v.make} {v.model}
                    </div>
                    <StatusPill status={v.status} />
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 font-mono">{v.plate || "—"} · VIN {v.vin || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div>
                  <div className="overline">Daily</div>
                  <div className="text-sm font-semibold tabular-nums">{money(v.daily_rate)}</div>
                </div>
                <div>
                  <div className="overline">Weekly</div>
                  <div className="text-sm font-semibold tabular-nums">{money(v.weekly_rate)}</div>
                </div>
                <div>
                  <div className="overline">Deposit</div>
                  <div className="text-sm font-semibold tabular-nums">{money(v.deposit_amount)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                <div>Mileage: <span className="tabular-nums">{Number(v.mileage || 0).toLocaleString()}</span></div>
                <div>GPS: {v.gps_device_id || "—"}</div>
                <div>Reg exp: {fmtDate(v.registration_expiration)}</div>
                <div>Inspect: {fmtDate(v.inspection_expiration)}</div>
              </div>
              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`}>
                  <Edit3 size={12} className="mr-1.5" /> Edit
                </Button>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => del(v.id)}
                    data-testid={`delete-vehicle-${v.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Year</Label>
                <Input type="number" data-testid="veh-year" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} />
              </div>
              <div>
                <Label>Make</Label>
                <Input data-testid="veh-make" value={editing.make} onChange={(e) => setEditing({ ...editing, make: e.target.value })} />
              </div>
              <div>
                <Label>Model</Label>
                <Input data-testid="veh-model" value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} />
              </div>
              <div>
                <Label>Plate</Label>
                <Input data-testid="veh-plate" value={editing.plate} onChange={(e) => setEditing({ ...editing, plate: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>VIN</Label>
                <Input data-testid="veh-vin" value={editing.vin} onChange={(e) => setEditing({ ...editing, vin: e.target.value })} />
              </div>
              <div>
                <Label>Mileage</Label>
                <Input type="number" data-testid="veh-mileage" value={editing.mileage} onChange={(e) => setEditing({ ...editing, mileage: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="veh-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Daily rate</Label>
                <Input type="number" step="0.01" data-testid="veh-daily" value={editing.daily_rate} onChange={(e) => setEditing({ ...editing, daily_rate: e.target.value })} />
              </div>
              <div>
                <Label>Weekly rate</Label>
                <Input type="number" step="0.01" data-testid="veh-weekly" value={editing.weekly_rate} onChange={(e) => setEditing({ ...editing, weekly_rate: e.target.value })} />
              </div>
              <div>
                <Label>Deposit amount</Label>
                <Input type="number" step="0.01" data-testid="veh-deposit" value={editing.deposit_amount} onChange={(e) => setEditing({ ...editing, deposit_amount: e.target.value })} />
              </div>
              <div>
                <Label>GPS device ID</Label>
                <Input data-testid="veh-gps" value={editing.gps_device_id} onChange={(e) => setEditing({ ...editing, gps_device_id: e.target.value })} />
              </div>
              <div>
                <Label>Insurance status</Label>
                <Input data-testid="veh-insurance" value={editing.insurance_status} onChange={(e) => setEditing({ ...editing, insurance_status: e.target.value })} placeholder="Active / Pending / Expired" />
              </div>
              <div>
                <Label>Registration expiration</Label>
                <Input type="date" data-testid="veh-reg-exp" value={editing.registration_expiration || ""} onChange={(e) => setEditing({ ...editing, registration_expiration: e.target.value })} />
              </div>
              <div>
                <Label>Inspection expiration</Label>
                <Input type="date" data-testid="veh-insp-exp" value={editing.inspection_expiration || ""} onChange={(e) => setEditing({ ...editing, inspection_expiration: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Maintenance notes</Label>
                <Textarea data-testid="veh-maint-notes" value={editing.maintenance_notes} onChange={(e) => setEditing({ ...editing, maintenance_notes: e.target.value })} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="veh-cancel">Cancel</Button>
            <Button onClick={save} className="bg-black hover:bg-gray-800 text-white" data-testid="veh-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
